/**
 * AI Scribe V2 — window header
 *
 * Live status + elapsed time, the note-template picker, the stop control and the
 * minimise/close chrome. The window is fixed, so this strip is not a drag handle.
 *
 * @author [Rajkumar Rathod]
 */

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { Box, Button, IconButton, Menu, MenuItem } from "@mui/material";
import { ReactNode, useState } from "react";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import { DEFAULT_FORM_LIST } from "@/constants/visit-note-form-constant";
import { useVisitNoteFormOptions } from "@/hooks/use-visit-note-form-options";
import { VisitNote } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/ActiveForm";
import { SCRIBE_V2_COLORS, SCRIBE_V2_DIMENSIONS } from "../constants";
import { ScribeStatus } from "../types";
import { formatElapsed } from "../utils";
import ListeningWaveIcon from "./ListeningWaveIcon";

/** Every visit-note template is offered — the picker mirrors the note list. */
const SCRIBE_NOTE_TYPES = DEFAULT_FORM_LIST;

const STATUS_LABEL: Record<ScribeStatus, string> = {
  IDLE: "Scribe ready",
  LISTENING: "Scribe is listening",
  PAUSED: "Listening paused",
  FINALIZING: "Drafting note",
  COMPLETED: "Session ended",
  ERROR: "Microphone unavailable",
};

interface ScribeWindowHeaderProps {
  status: ScribeStatus;
  elapsedSeconds: number;
  noteType: VisitNote;
  onNoteTypeChange: (value: VisitNote) => void;
  onStop: () => void;
  onMinimizeToggle: () => void;
  onClose: () => void;
  isMinimized: boolean;
  /** Lock the template picker once drafting — changing it breaks the drafted note. */
  isNoteTypeLocked?: boolean;
  /**
   * Primary action for the current step (e.g. "Push to EHR"), rendered next to
   * the window chrome. A slot so the header stays unaware of what the step does.
   */
  actionSlot?: ReactNode;
}

const ScribeWindowHeader = ({
  status,
  elapsedSeconds,
  noteType,
  onNoteTypeChange,
  onStop,
  onMinimizeToggle,
  onClose,
  isMinimized,
  isNoteTypeLocked,
  actionSlot,
}: ScribeWindowHeaderProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { forms: noteTypeForms } = useVisitNoteFormOptions();

  const isLive = status === "LISTENING";
  const isPaused = status === "PAUSED";
  const accent = isPaused
    ? SCRIBE_V2_COLORS.accentPaused
    : isLive
      ? SCRIBE_V2_COLORS.accentLive
      : SCRIBE_V2_COLORS.textSecondary;

  const noteTypeLabel =
    SCRIBE_NOTE_TYPES.find((form) => form.value === noteType)?.name ??
    "Select note type";

  const chromeButtonSx = {
    p: px(PRIMITIVE.spacing[4]),
    color: SCRIBE_V2_COLORS.textSecondary,
    "& svg": { fontSize: px(PRIMITIVE.spacing[16]) },
  };

  return (
    <Flex
      alignItems="center"
      gap={0.75}
      px={px(PRIMITIVE.spacing[12])}
      sx={{
        height: SCRIBE_V2_DIMENSIONS.headerHeight,
        flexShrink: 0,
        userSelect: "none",
        backgroundColor: SCRIBE_V2_COLORS.headerBg,
        borderBottom: `1px solid ${SCRIBE_V2_COLORS.panelDivider}`,
      }}
    >
      <ListeningWaveIcon color={accent} animated={isLive} />

      <Text
        noWrap
        sx={{
          color: accent,
          fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
          fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
        }}
      >
        {STATUS_LABEL[status]}
        {status !== "IDLE" ? ` · ${formatElapsed(elapsedSeconds)}` : ""}
      </Text>

      {!isMinimized && (
        <>
          <Text sx={{ color: SCRIBE_V2_COLORS.textMuted }}>·</Text>

          <Button
            size="small"
            disableRipple
            disabled={isNoteTypeLocked}
            title={
              isNoteTypeLocked
                ? "Template is locked once the note is drafted"
                : undefined
            }
            onClick={
              isNoteTypeLocked
                ? undefined
                : (event) => setMenuAnchor(event.currentTarget)
            }
            startIcon={<ArticleOutlinedIcon />}
            endIcon={
              isNoteTypeLocked ? undefined : <KeyboardArrowDownOutlinedIcon />
            }
            sx={{
              p: 0,
              minWidth: "auto",
              color: SCRIBE_V2_COLORS.bubbleProviderName,
              fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
              fontWeight: PRIMITIVE.typography.fontWeight.regular,
              textTransform: "none",
              "& svg": { fontSize: px(PRIMITIVE.spacing[16]) },
              "& .MuiButton-startIcon": { mr: 0.5 },
              "& .MuiButton-endIcon": { ml: 0.25 },
              "&:hover": { backgroundColor: "transparent" },
              /** Keep the chosen template legible when locked — read-only, not greyed out. */
              "&.Mui-disabled": { color: SCRIBE_V2_COLORS.bubbleProviderName },
            }}
          >
            {noteTypeLabel}
          </Button>

          {!isNoteTypeLocked && (
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              {noteTypeForms.map((form) => (
                <MenuItem
                  key={form.value}
                  selected={form.value === noteType}
                  onClick={() => {
                    onNoteTypeChange(form.value);
                    setMenuAnchor(null);
                  }}
                  sx={{ fontSize: px(PRIMITIVE.typography.fontSize.body.b4) }}
                >
                  {form.name}
                </MenuItem>
              ))}
            </Menu>
          )}

          {/* Solid square = stop, matching the design's compact glyph */}
          <IconButton
            size="small"
            title="Stop session"
            aria-label="Stop session"
            disabled={!isLive && !isPaused}
            onClick={onStop}
            sx={{
              p: px(PRIMITIVE.spacing[4]),
              "&:hover .stop-glyph": {
                backgroundColor: SCRIBE_V2_COLORS.stopGlyphHover,
              },
            }}
          >
            <Box
              className="stop-glyph"
              sx={{
                width: px(PRIMITIVE.spacing[12]),
                height: px(PRIMITIVE.spacing[12]),
                borderRadius: px(PRIMITIVE.radius[2]),
                backgroundColor:
                  isLive || isPaused
                    ? SCRIBE_V2_COLORS.stopGlyph
                    : SCRIBE_V2_COLORS.stopGlyphDisabled,
              }}
            />
          </IconButton>
        </>
      )}

      <Flex gap={0.75} alignItems="center" ml="auto">
        {!isMinimized && actionSlot}

        <IconButton
          size="small"
          title={isMinimized ? "Expand" : "Minimise"}
          aria-label={isMinimized ? "Expand" : "Minimise"}
          onClick={onMinimizeToggle}
          sx={chromeButtonSx}
        >
          {isMinimized ? <OpenInFullOutlinedIcon /> : <RemoveOutlinedIcon />}
        </IconButton>
        <IconButton
          size="small"
          title="Close"
          aria-label="Close"
          onClick={onClose}
          sx={chromeButtonSx}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </Flex>
    </Flex>
  );
};

export default ScribeWindowHeader;
